// index:App:TokenComponent

import React, { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useMsal } from '@azure/msal-react'
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser'
import { useIsAuthenticated } from '@azure/msal-react'

import defaultStore from './util/Globale'
import { profileService, navigationService, dcpsConfig } from './util/Services'
import { userActions } from './store/user-slice'
import { MenuState, menuActions } from './store/menu-slice'
import { tokenActions } from './store/token-slice'
import { registerServerError } from './store/error-slice'
import { initializeIcons } from '@fluentui/react'
import { userSettingsService } from './services/UserSettingsService'

import './style/App.css'
// Explicitly import Fluent UI core styles to ensure font-face rules are loaded
import '@fluentui/react/dist/css/fabric.min.css'

import initFontAwesome from './util/FontAwesome'
import { registerCustomIcons } from './util/CustomIcons'
import { setupPassiveEvents } from './util/PassiveEvents'

import TokenComponent from './TokenComponent'
import { ServerErrorResponse, isServerErrorResponse } from './adapters/ApiSchema'
import { UserMenuResponseData } from './services/Navigation'
import { queryKnownMatchMedia } from './adapters/windowUtils'

// Remove comments from Prod
if (defaultStore.isProduction) {
  // console.log = () => { };
  // console.error = () => { };
  // console.debug = () => { };
}

// Setup passive event listeners to improve scroll performance
setupPassiveEvents()

// First initialize all default fluent ui icons
initializeIcons()
// Then register our custom icons that might not be in the default set
registerCustomIcons()
// Initialize FontAwesome icons
initFontAwesome()

function App() {
  const { instance, accounts, inProgress } = useMsal()

  const dispatch = useDispatch()

  const isAuthenticated = useIsAuthenticated()

  const APIsToConnect = useCallback(
    (res: AuthenticationResult) => {
      // console.log('apis to connect', res.accessToken);
      if (res.expiresOn) {
        let payload = {
          token: res.accessToken,
          expiration: new Date(res.expiresOn).toLocaleString(),
        }
        dispatch(tokenActions.updateToken(payload))
      }

      profileService.getUserProfileInfo().then(result => {
        if (!isServerErrorResponse(result)) {
          let payload = {
            user: result,
          }
          // console.log('user profile loaded')
          dispatch(userActions.updateUser(payload))

          // Check for beta site opt-in after successful profile load
          userSettingsService
            .getUserSettings()
            .then(settings => {
              if (settings.user.beta_site_opt_in) {
                console.log('[App] User opted into beta site, redirecting to betaview.duvalschools.org')
                window.location.href = 'https://betaview.duvalschools.org/'
              }
            })
            .catch(error => {
              console.warn('[App] Failed to check beta opt-in preference:', error)
            })
        } else {
          // console.warn(result) // Keep registerServerError, comment out direct console warning
          registerServerError({ dispatch, e: result, type: 'user', fatal: true })
        }
      })

      navigationService.getUserMenu().then((result: UserMenuResponseData | ServerErrorResponse) => {
        // console.log(result);
        if (!isServerErrorResponse(result)) {
          let isSmallDevice = queryKnownMatchMedia('isSmallDevice') ?? false
          // const items = Object.keys(result.data).includes("Items") ? (result.data as UserMenuResponseData)?.Items : undefined;
          // if (result.status === 200 && items && Array.isArray(items)) {
          let payload: MenuState = {
            menu: result.Items,
            mainHamburgerOpen: !isSmallDevice,
          }
          // console.log('dispatching hamburger open', payload.mainHamburgerOpen);
          dispatch(menuActions.update(payload))
        } else {
          // console.error('getUserMenu', result) // Keep registerServerError, comment out direct console error
          registerServerError({ dispatch, e: result, type: 'menu', fatal: true })
        }
      })
    },
    [dispatch]
  )

  // TODO: the token callout response does not have an expiresOn property
  // this appears it should be an AuthenticationResult
  const updateNewToken = useCallback(
    (res: AuthenticationResult) => {
      if (res.expiresOn) {
        // console.log('updateNewToken', JSON.stringify(res));
        let payload = {
          token: res.accessToken,
          // typescript thinks this shouldn't work, but it does
          expiration: new Date(res.expiresOn).toLocaleString(),
        }
        dispatch(tokenActions.updateToken(payload))
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (!accounts || accounts.length < 1) return
    const requestNew = {
      ...dcpsConfig,
      account: accounts[0],
    }

    function RequestAccessToken() {
      //call to get token

      instance
        .acquireTokenSilent(requestNew)
        .then(res => {
          APIsToConnect(res)
          //console.log("request2-new", res);
          updateNewToken(res)
        })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(requestNew)
          } else {
            // console.error('requestAccessToken error', error)
          }
        })
    }

    if (isAuthenticated && inProgress === 'none' && accounts.length !== 0) {
      // console.log('requesting access token', JSON.stringify({ isAuthenticated, inProgress, accounts: accounts.length }))
      RequestAccessToken()
    }
  }, [APIsToConnect, accounts, accounts.length, inProgress, instance, isAuthenticated, updateNewToken])

  return (
    <React.Fragment>
      <TokenComponent />
    </React.Fragment>
  )
}

export default App
